import React from 'react';
import { Checkbox, Form, Input, Switch, Typography } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import type { FormInstance } from 'antd/es/form';

export const TONE_OPTIONS = [
  { value: 'cercano', label: 'Cercano', hint: 'De tú, cálido, como alguien del negocio' },
  { value: 'profesional', label: 'Profesional', hint: 'Claro y directo, sin ser frío' },
  { value: 'formal', label: 'Formal', hint: 'De usted, serio, típico de consultorio' },
  { value: 'alegre', label: 'Alegre', hint: 'Entusiasta y ligero, típico de barbería' },
]

export const JOB_OPTIONS = [
  { id: 'agendar', label: 'Agendar citas', hint: 'Crea la cita en el calendario', keywords: ['agendar', 'agenda'] },
  { id: 'horarios', label: 'Mostrar horarios disponibles', hint: 'Dice qué huecos hay', keywords: ['horario', 'horarios'] },
  { id: 'consultar', label: 'Consultar citas ya agendadas', hint: 'Lista las citas del cliente', keywords: ['consultar', 'ver citas', 'mis citas'] },
  { id: 'reprogramar', label: 'Reprogramar una cita', hint: 'Cambia día u hora', keywords: ['reprogram'] },
  { id: 'cancelar', label: 'Cancelar una cita', hint: 'Borra la cita si el cliente lo pide', keywords: ['cancel'] },
]

export const DEFAULT_JOBS = ['agendar', 'horarios']

export const AVOID_OPTIONS = [
  { id: 'precios', label: 'Hablar de precios', keywords: ['precio', 'precios'] },
  { id: 'insistir', label: 'Insistir si no quiere agendar', keywords: ['insistir'] },
  { id: 'repetir', label: 'Repetir preguntas ya contestadas', keywords: ['repetir'] },
  { id: 'inventar', label: 'Inventar horarios o datos', keywords: ['inventar'] },
  { id: 'prometer', label: 'Prometer lo que no puede cumplir', keywords: ['prometer'] },
]

export const PERSONA_QUESTION_COUNT = 3
export const PERSONA_SUMMARY_STEP = 3

export type PersonaAnswers = {
  tone?: string
  persona_jobs?: string[]
  persona_avoid?: string[]
  persona_avoid_extra?: string
}

const labelsOf = (
  ids: string[] | undefined,
  options: { id: string; label: string }[],
): string[] => (ids || []).map((id) => options.find((o) => o.id === id)?.label || id).filter(Boolean)

export const assemblePurpose = (answers: PersonaAnswers): string => {
  const jobs = labelsOf(answers.persona_jobs, JOB_OPTIONS)
  const avoid = [
    ...labelsOf(answers.persona_avoid, AVOID_OPTIONS).map((label) => label.toLowerCase()),
    (answers.persona_avoid_extra || '').trim(),
  ].filter(Boolean)
  const parts: string[] = []
  if (jobs.length) parts.push(jobs.join(', '))
  if (avoid.length) parts.push(`No debe: ${avoid.join(', ')}`)
  return parts.join('. ').slice(0, 500)
}

export const hydratePersonaFromPurpose = (purpose: string): PersonaAnswers => {
  const text = (purpose || '').toLowerCase()
  const jobs = JOB_OPTIONS.filter((o) => o.keywords.some((k) => text.includes(k))).map((o) => o.id)
  const avoid = AVOID_OPTIONS.filter((o) => o.keywords.some((k) => text.includes(k))).map((o) => o.id)
  return {
    persona_jobs: jobs.length ? jobs : DEFAULT_JOBS,
    persona_avoid: avoid,
    persona_avoid_extra: '',
  }
}

const ChoiceCard: React.FC<{
  selected: boolean
  title: string
  hint?: string
  disabled?: boolean
  onClick: () => void
}> = ({ selected, title, hint, disabled, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    style={{
      textAlign: 'left',
      padding: '14px 16px',
      borderRadius: 10,
      border: selected ? '1px solid #1677ff' : '1px solid #d9d9d9',
      background: selected ? '#e6f4ff' : '#fff',
      boxShadow: selected ? '0 0 0 1px #1677ff' : undefined,
      cursor: disabled ? 'not-allowed' : 'pointer',
      width: '100%',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
      <strong>{title}</strong>
      {selected ? <CheckCircleFilled style={{ color: '#1677ff' }} /> : null}
    </div>
    {hint ? <div style={{ color: '#8c8c8c', marginTop: 4, fontSize: 13 }}>{hint}</div> : null}
  </button>
)

const BotPersonalityOnboarding: React.FC<{
  form: FormInstance
  quizStep: number
  disabled?: boolean
  onJump?: (step: number) => void
}> = ({ form, quizStep, disabled, onJump }) => {
  const tone: string = Form.useWatch('tone', form) || 'cercano'
  const jobsWatch = Form.useWatch('persona_jobs', form)
  const avoidWatch = Form.useWatch('persona_avoid', form)
  const jobs: string[] = Array.isArray(jobsWatch) ? jobsWatch : []
  const avoid: string[] = Array.isArray(avoidWatch) ? avoidWatch : []
  const avoidExtra: string = Form.useWatch('persona_avoid_extra', form) || ''
  const isActive: boolean = Form.useWatch('is_active', form) !== false

  const toggle = (field: 'persona_jobs' | 'persona_avoid', id: string, current: string[]) => {
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    form.setFieldsValue({ [field]: next })
  }

  const toneLabel = TONE_OPTIONS.find((o) => o.value === tone)
  const jobLabels = labelsOf(jobs, JOB_OPTIONS)
  const avoidLabels = labelsOf(avoid, AVOID_OPTIONS)
  const inQuestion = quizStep < PERSONA_SUMMARY_STEP

  return (
    <div>
      {inQuestion ? (
        <>
          <div style={{ color: '#8c8c8c', marginBottom: 6, fontSize: 13 }}>
            Pregunta {quizStep + 1} de {PERSONA_QUESTION_COUNT}
          </div>
          <div style={{ height: 4, background: '#f0f0f0', borderRadius: 4, marginBottom: 20 }}>
            <div
              style={{
                width: `${((quizStep + 1) / PERSONA_QUESTION_COUNT) * 100}%`,
                height: '100%',
                background: '#1677ff',
                borderRadius: 4,
                transition: 'width 200ms ease',
              }}
            />
          </div>
        </>
      ) : (
        <div style={{ color: '#8c8c8c', marginBottom: 16, fontSize: 13 }}>Resumen de la personalidad</div>
      )}

      <Form.Item name="tone" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="persona_jobs" hidden>
        <Checkbox.Group />
      </Form.Item>
      <Form.Item name="persona_avoid" hidden>
        <Checkbox.Group />
      </Form.Item>
      <Form.Item name="purpose" hidden>
        <Input />
      </Form.Item>

      <div style={{ display: quizStep === 0 ? 'block' : 'none' }}>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          ¿Cómo debe hablar con tus clientes?
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          Elige una. Así arma las frases, si habla de tú o de usted, y el tono del saludo.
        </Typography.Paragraph>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {TONE_OPTIONS.map((opt) => (
            <ChoiceCard
              key={opt.value}
              selected={tone === opt.value}
              title={opt.label}
              hint={opt.hint}
              disabled={disabled}
              onClick={() => form.setFieldsValue({ tone: opt.value })}
            />
          ))}
        </div>
      </div>

      <div style={{ display: quizStep === 1 ? 'block' : 'none' }}>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          ¿Qué debe hacer por el cliente?
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          Solo lo que el bot ya sabe hacer. Marca varias. El calendario se elige después, en Aplicaciones.
        </Typography.Paragraph>
        <div style={{ display: 'grid', gap: 10 }}>
          {JOB_OPTIONS.map((opt) => (
            <ChoiceCard
              key={opt.id}
              selected={jobs.includes(opt.id)}
              title={opt.label}
              hint={opt.hint}
              disabled={disabled}
              onClick={() => toggle('persona_jobs', opt.id, jobs)}
            />
          ))}
        </div>
      </div>

      <div style={{ display: quizStep === 2 ? 'block' : 'none' }}>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          ¿Qué debe evitar?
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          Opcional. Marca lo que no quieres que haga, aunque el cliente lo pida.
        </Typography.Paragraph>
        <div style={{ display: 'grid', gap: 10 }}>
          {AVOID_OPTIONS.map((opt) => (
            <ChoiceCard
              key={opt.id}
              selected={avoid.includes(opt.id)}
              title={opt.label}
              disabled={disabled}
              onClick={() => toggle('persona_avoid', opt.id, avoid)}
            />
          ))}
        </div>
        <Form.Item name="persona_avoid_extra" label="¿Otra cosa que no deba hacer?" style={{ marginTop: 16, marginBottom: 0 }}>
          <Input.TextArea
            rows={2}
            maxLength={240}
            showCount
            placeholder="Ej: no hablar de promociones, no pedir datos de tarjeta"
          />
        </Form.Item>
      </div>

      <div style={{ display: quizStep === PERSONA_SUMMARY_STEP ? 'block' : 'none' }}>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          Así va a ser tu bot
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          Revisa lo que elegiste. Si algo no cuadra, cámbialo antes de seguir.
        </Typography.Paragraph>
        <div
          style={{
            border: '1px solid #f0f0f0',
            borderRadius: 12,
            padding: 16,
            background: '#fafafa',
            display: 'grid',
            gap: 14,
          }}
        >
          <SummaryRow
            label="Habla"
            value={toneLabel ? `${toneLabel.label}. ${toneLabel.hint}` : tone}
            onEdit={onJump ? () => onJump(0) : undefined}
          />
          <SummaryRow
            label="Hace"
            value={jobLabels.join(' · ') || 'Aún no definiste tareas'}
            onEdit={onJump ? () => onJump(1) : undefined}
          />
          <SummaryRow
            label="Evita"
            value={[...avoidLabels, avoidExtra.trim()].filter(Boolean).join(' · ') || 'Sin restricciones extra'}
            onEdit={onJump ? () => onJump(2) : undefined}
          />
        </div>
        <Form.Item label="Bot activo" name="is_active" valuePropName="checked" style={{ marginTop: 16, marginBottom: 0 }}>
          <Switch checked={isActive} />
        </Form.Item>
      </div>
    </div>
  )
}

const SummaryRow: React.FC<{ label: string; value: string; onEdit?: () => void }> = ({ label, value, onEdit }) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
      <div style={{ fontSize: 12, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          style={{ border: 0, background: 'none', color: '#1677ff', cursor: 'pointer', padding: 0 }}
        >
          Cambiar
        </button>
      ) : null}
    </div>
    <div style={{ marginTop: 4 }}>{value}</div>
  </div>
)

export default BotPersonalityOnboarding;
